<template>
  <div class="mx-auto p-4 bg-warm-gray-900 min-h-screen">
    <div class="container mx-auto">
      <div class="flex items-center justify-between mb-6">
        <h1 class="text-2xl font-bold text-gray-100">Endorsers</h1>
      </div>

      <!-- Tabs -->
      <div class="flex items-center gap-3 mb-6">
        <button
          @click="activeTab = 'endorsers'"
          :class="[
            'px-4 py-2 rounded text-sm font-medium transition-colors',
            activeTab === 'endorsers'
              ? 'bg-blue-600 text-white'
              : 'bg-warm-gray-800 text-gray-300 hover:bg-warm-gray-700'
          ]"
        >
          Endorsers ({{ endorsers.length }})
        </button>
        <button
          @click="activeTab = 'payouts'"
          :class="[
            'px-4 py-2 rounded text-sm font-medium transition-colors',
            activeTab === 'payouts'
              ? 'bg-blue-600 text-white'
              : 'bg-warm-gray-800 text-gray-300 hover:bg-warm-gray-700'
          ]"
        >
          Payouts ({{ pendingPayouts.length }})
        </button>
      </div>
      
      <!-- Filters and Total -->
      <div v-if="activeTab === 'endorsers'" class="mb-6">
        <div class="flex items-center justify-between">
          <div class="text-lg font-semibold text-gray-100">
            Total: {{ endorsers.length }} endorser{{ endorsers.length !== 1 ? 's' : '' }}
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="activeTab === 'endorsers' && loading" class="text-center py-12">
        <div class="text-gray-400">Loading endorsers...</div>
      </div>

      <!-- Table -->
      <div
        v-else-if="activeTab === 'endorsers' && endorsers.length > 0"
        :class="['bg-warm-gray-800 rounded-lg overflow-hidden border border-warm-gray-700', isUpdating ? 'opacity-50 pointer-events-none' : '']"
      >
        <div class="overflow-x-auto">
          <table class="w-full text-sm text-left">
            <thead class="text-xs text-gray-400 uppercase border-b border-warm-gray-700">
              <tr>
                <th class="px-4 py-3 cursor-pointer select-none hover:text-gray-200" @click="toggleSort('firstName')">
                  First Name
                  <span v-if="sortKey === 'firstName'">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
                </th>
                <th class="px-4 py-3">Last Name</th>
                <th class="px-4 py-3">Email</th>
                <th class="px-4 py-3">Personal Email</th>
                <th class="px-4 py-3 cursor-pointer select-none hover:text-gray-200" @click="toggleSort('commissionAmount')">
                  Commission Amount
                  <span v-if="sortKey === 'commissionAmount'">{{ sortDir === 'asc' ? '↑' : '↓' }}</span>
                </th>
                <th class="px-4 py-3">Discount Amount</th>
                <th class="px-4 py-3">Max Coupons</th>
                <th class="px-4 py-3">Payout Threshold</th>
                <th class="px-4 py-3">Running Commission</th>
                <th class="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody class="text-gray-300">
              <tr
                v-for="endorser in sortedEndorsers"
                :key="endorser.id"
                class="border-b border-warm-gray-800 transition-all hover:bg-gray-800/50"
              >
                <td class="px-4 py-3">
                  <div class="font-medium text-gray-100">{{ endorser.data.firstName || 'N/A' }}</div>
                </td>
                <td class="px-4 py-3">
                  <div class="font-medium text-gray-100">{{ endorser.data.lastName || 'N/A' }}</div>
                </td>
                <td class="px-4 py-3">
                  <div class="text-gray-100">{{ endorser.data.email || 'N/A' }}</div>
                </td>
                <td class="px-4 py-3">
                  <div class="text-gray-100">{{ endorser.data.personalEmail || 'N/A' }}</div>
                </td>
                <td class="px-4 py-3 text-gray-100">
                  ₱{{ formatCurrency(endorser.data.commissionAmount || 0) }}
                </td>
                <td class="px-4 py-3 text-gray-100">
                  ₱{{ formatCurrency(endorser.data.discountAmount || 0) }}
                </td>
                <td class="px-4 py-3 text-gray-100">
                  {{ endorser.data.maxCoupons || 0 }}
                </td>
                <td class="px-4 py-3 text-gray-100">
                  ₱{{ formatCurrency(endorser.data.payoutThreshold || 0) }}
                </td>
                <td class="px-4 py-3 font-medium" :class="(runningCommissionByEmail[endorser.data.email] || 0) > 0 ? 'text-green-400' : 'text-gray-100'">
                  ₱{{ formatCurrency(runningCommissionByEmail[endorser.data.email] || 0) }}
                </td>
                <td class="px-4 py-3">
                  <div class="flex items-center gap-2">
                    <button
                      @click="$router.push(`/endorsers/${endorser.id}`)"
                      :disabled="loading"
                      class="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      View
                    </button>
                    <button
                      @click="openEditModal(endorser)"
                      :disabled="isUpdating || loading"
                      class="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Edit
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else-if="activeTab === 'endorsers' && !loading" class="text-center py-12">
        <div class="text-gray-400">No endorsers found</div>
      </div>

      <!-- Payouts Tab -->
      <div v-else-if="activeTab === 'payouts'">
        <div class="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div class="text-lg font-semibold text-gray-100">
            Pending: {{ pendingPayouts.length }} payout{{ pendingPayouts.length !== 1 ? 's' : '' }}
          </div>
          <div class="flex items-center gap-2">
            <button
              @click="payoutFilter = 'all'"
              :class="[
                'px-3 py-1.5 rounded text-xs font-medium transition-colors',
                payoutFilter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-warm-gray-800 text-gray-300 hover:bg-warm-gray-700'
              ]"
            >
              All
            </button>
            <button
              @click="payoutFilter = 'pending'"
              :class="[
                'px-3 py-1.5 rounded text-xs font-medium transition-colors',
                payoutFilter === 'pending'
                  ? 'bg-blue-600 text-white'
                  : 'bg-warm-gray-800 text-gray-300 hover:bg-warm-gray-700'
              ]"
            >
              Pending Requests
            </button>
          </div>
        </div>

        <!-- Payouts Loading State -->
        <div v-if="payoutsLoading" class="text-center py-12">
          <div class="text-gray-400">Loading payouts...</div>
        </div>

        <!-- Payouts Error State -->
        <div v-else-if="payoutsError" class="p-4 bg-red-900 border border-red-700 rounded mb-4">
          <p class="text-red-200">{{ payoutsError }}</p>
        </div>

        <!-- Payouts Table -->
        <div
          v-else-if="filteredPayouts.length > 0"
          class="bg-warm-gray-800 rounded-lg overflow-hidden border border-warm-gray-700"
        >
          <div class="overflow-x-auto">
            <table class="w-full text-sm text-left">
              <thead class="text-xs text-gray-400 uppercase border-b border-warm-gray-700">
                <tr>
                  <th class="px-4 py-3">Requested</th>
                  <th class="px-4 py-3">Status</th>
                  <th class="px-4 py-3">Endorser</th>
                  <th class="px-4 py-3">Email</th>
                  <th class="px-4 py-3">Amount</th>
                  <th class="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody class="text-gray-300">
                <tr
                  v-for="payout in filteredPayouts"
                  :key="payout.id"
                  class="border-b border-warm-gray-800 transition-all hover:bg-gray-800/50"
                >
                  <td class="px-4 py-3 text-gray-400">
                    {{ formatDate(payout.data.requestedAt) }}
                  </td>
                  <td class="px-4 py-3">
                    <span
                      class="px-2 py-1 text-xs font-medium rounded"
                      :class="getPayoutStatusClass(payout.data.status)"
                    >
                      {{ getPayoutStatusLabel(payout.data.status) }}
                    </span>
                  </td>
                  <td class="px-4 py-3 text-gray-100">
                    {{ getEndorserName(payout) }}
                  </td>
                  <td class="px-4 py-3 text-gray-100">
                    {{ payout.data.endorserEmail || 'N/A' }}
                  </td>
                  <td class="px-4 py-3 text-gray-100 font-medium">
                    ₱{{ formatCurrency(payout.data.totalAmount || 0) }}
                  </td>
                  <td class="px-4 py-3">
                    <button
                      v-if="payout.data.endorserId"
                      @click="$router.push(`/endorsers/${payout.data.endorserId}`)"
                      class="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-medium transition-colors"
                    >
                      View Endorser
                    </button>
                    <span v-else class="text-gray-500 text-xs">—</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Payouts Empty State -->
        <div v-else class="text-center py-12 text-gray-400">
          <div v-if="payoutFilter === 'pending'">No pending payout requests.</div>
          <div v-else>No payouts found.</div>
        </div>
      </div>

      <!-- Edit Endorser Modal -->
      <div
        v-if="isEditModalOpen"
        class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      >
        <div class="bg-warm-gray-800 rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-xl font-bold text-gray-100">Edit Endorser</h2>
            <button
              @click="closeEditModal"
              :disabled="isUpdating"
              class="text-gray-400 hover:text-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form @submit.prevent="handleUpdateEndorser" class="space-y-4">
            <!-- Email (Read-only) -->
            <div class="bg-warm-gray-900 rounded p-3 mb-4">
              <p class="text-sm text-gray-400">Email (System Email):</p>
              <p class="text-lg font-semibold text-gray-100">{{ editFormData.email }}</p>
              <p class="text-xs text-gray-500 mt-1">This email cannot be changed</p>
            </div>

            <!-- First Name -->
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2">
                First Name <span class="text-red-400">*</span>
              </label>
              <input
                v-model="editFormData.firstName"
                type="text"
                required
                :disabled="isUpdating"
                class="w-full px-3 py-2 bg-warm-gray-900 border border-warm-gray-700 rounded text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Enter first name"
              />
            </div>

            <!-- Last Name -->
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2">
                Last Name <span class="text-red-400">*</span>
              </label>
              <input
                v-model="editFormData.lastName"
                type="text"
                required
                :disabled="isUpdating"
                class="w-full px-3 py-2 bg-warm-gray-900 border border-warm-gray-700 rounded text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Enter last name"
              />
            </div>

            <!-- Personal Email -->
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2">
                Personal Email <span class="text-red-400">*</span>
              </label>
              <input
                v-model="editFormData.personalEmail"
                type="email"
                required
                :disabled="isUpdating"
                class="w-full px-3 py-2 bg-warm-gray-900 border border-warm-gray-700 rounded text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Enter personal email"
              />
            </div>

            <!-- Commission Amount -->
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2">
                Commission Amount <span class="text-red-400">*</span>
              </label>
              <div class="relative">
                <span class="absolute left-3 top-2 text-gray-400">₱</span>
                <input
                  v-model.number="editFormData.commissionAmount"
                  type="number"
                  min="0"
                  step="0.01"
                  required
                  :disabled="isUpdating"
                  class="w-full pl-8 pr-3 py-2 bg-warm-gray-900 border border-warm-gray-700 rounded text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="0.00"
                />
              </div>
            </div>

            <!-- Discount Amount -->
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2">
                Discount Amount <span class="text-red-400">*</span>
              </label>
              <div class="relative">
                <span class="absolute left-3 top-2 text-gray-400">₱</span>
                <input
                  v-model.number="editFormData.discountAmount"
                  type="number"
                  min="0"
                  step="0.01"
                  required
                  :disabled="isUpdating"
                  class="w-full pl-8 pr-3 py-2 bg-warm-gray-900 border border-warm-gray-700 rounded text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="0.00"
                />
              </div>
            </div>

            <!-- Max Coupons -->
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2">
                Max Coupons <span class="text-red-400">*</span>
              </label>
              <input
                v-model.number="editFormData.maxCoupons"
                type="number"
                min="1"
                required
                :disabled="isUpdating"
                class="w-full px-3 py-2 bg-warm-gray-900 border border-warm-gray-700 rounded text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Enter max coupons"
              />
            </div>

            <!-- Payout Threshold -->
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2">
                Payout Threshold <span class="text-red-400">*</span>
              </label>
              <div class="relative">
                <span class="absolute left-3 top-2 text-gray-400">₱</span>
                <input
                  v-model.number="editFormData.payoutThreshold"
                  type="number"
                  min="0"
                  step="0.01"
                  required
                  :disabled="isUpdating"
                  class="w-full pl-8 pr-3 py-2 bg-warm-gray-900 border border-warm-gray-700 rounded text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="0.00"
                />
              </div>
              <p class="text-xs text-gray-400 mt-1">Minimum commission amount required before endorser can request a payout</p>
            </div>

            <!-- Actions -->
            <div class="flex gap-3 pt-4">
              <button
                type="button"
                @click="closeEditModal"
                :disabled="isUpdating"
                class="flex-1 px-4 py-2 bg-warm-gray-700 hover:bg-warm-gray-600 text-gray-300 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                :disabled="isUpdating"
                class="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="isUpdating">Updating...</span>
                <span v-else>Update</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { db } from '@/firebase/config.js'
import { collection, getDocs, doc, updateDoc, query, orderBy, where, onSnapshot } from 'firebase/firestore'

// Reactive state
const endorsers = ref([])
const loading = ref(false)
const isUpdating = ref(false)

const allUsedCoupons = ref([])
let unsubscribeAllUsedCoupons = null

const runningCommissionByEmail = computed(() => {
  const map = {}
  allUsedCoupons.value.forEach(coupon => {
    if (!coupon.data.isPaid) {
      const email = coupon.data.assignedToEmail
      if (email) map[email] = (map[email] || 0) + (coupon.data.commissionAmount || 0)
    }
  })
  return map
})

const setupAllUsedCouponsListener = () => {
  const q = query(collection(db, 'coupons'), where('status', '==', 'used'))
  unsubscribeAllUsedCoupons = onSnapshot(q, (snapshot) => {
    allUsedCoupons.value = snapshot.docs.map(doc => ({ id: doc.id, data: doc.data() }))
  }, (err) => {
    console.error('Error listening to all used coupons:', err)
  })
}

const activeTab = ref('endorsers')

// Payouts state
const payouts = ref([])
const payoutsLoading = ref(false)
const payoutsError = ref(null)
const payoutFilter = ref('pending')

// Edit Modal State
const isEditModalOpen = ref(false)
const editingEndorserId = ref(null)
const editFormData = ref({
  firstName: '',
  lastName: '',
  email: '',
  personalEmail: '',
  commissionAmount: 0,
  discountAmount: 0,
  maxCoupons: 0,
  payoutThreshold: 0
})

const sortKey = ref('commissionAmount')
const sortDir = ref('desc')

const toggleSort = (key) => {
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortDir.value = 'desc'
  }
}

const sortedEndorsers = computed(() => {
  return [...endorsers.value].sort((a, b) => {
    let aVal, bVal
    if (sortKey.value === 'firstName') {
      aVal = (a.data.firstName || '').toLowerCase()
      bVal = (b.data.firstName || '').toLowerCase()
    } else {
      aVal = a.data.commissionAmount || 0
      bVal = b.data.commissionAmount || 0
    }
    if (aVal < bVal) return sortDir.value === 'asc' ? -1 : 1
    if (aVal > bVal) return sortDir.value === 'asc' ? 1 : -1
    return 0
  })
})

const endorsersById = computed(() => {
  const map = {}
  endorsers.value.forEach(endorser => {
    map[endorser.id] = endorser
  })
  return map
})

const pendingPayouts = computed(() => {
  return payouts.value.filter(payout => payout.data.status === 'requested')
})

const filteredPayouts = computed(() => {
  if (payoutFilter.value === 'pending') {
    return pendingPayouts.value
  }
  return payouts.value
})

/**
 * Format currency amount
 */
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount)
}

/**
 * Format date from Firestore timestamp
 */
const formatDate = (timestamp) => {
  if (!timestamp) return 'N/A'

  let date
  if (timestamp?.toDate) {
    date = timestamp.toDate()
  } else if (timestamp instanceof Date) {
    date = timestamp
  } else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
    date = new Date(timestamp)
  } else {
    return 'N/A'
  }

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date)
}

/**
 * Get CSS classes for payout status badge
 */
const getPayoutStatusClass = (status) => {
  const statusClasses = {
    requested: 'bg-yellow-900 text-yellow-200',
    approved: 'bg-blue-900 text-blue-200',
    paid: 'bg-green-900 text-green-200',
    completed: 'bg-purple-900 text-purple-200',
    revoked: 'bg-red-900 text-red-200'
  }
  return statusClasses[status] || 'bg-gray-700 text-gray-300'
}

/**
 * Get display label for payout status
 */
const getPayoutStatusLabel = (status) => {
  const statusLabels = {
    requested: 'REQUESTED',
    approved: 'APPROVED',
    paid: 'PAID',
    completed: 'COMPLETED',
    revoked: 'REVOKED'
  }
  return statusLabels[status] || status.toUpperCase()
}

/**
 * Get endorser display name for payout
 */
const getEndorserName = (payout) => {
  const endorser = endorsersById.value[payout.data.endorserId]
  if (!endorser) return 'N/A'
  const first = endorser.data.firstName || ''
  const last = endorser.data.lastName || ''
  const name = `${first} ${last}`.trim()
  return name || 'N/A'
}

/**
 * Fetch all endorsers from Firestore
 */
const fetchEndorsers = async () => {
  loading.value = true
  try {
    const endorsersRef = collection(db, 'endorsers')
    const snapshot = await getDocs(endorsersRef)
    
    endorsers.value = snapshot.docs.map(doc => ({
      id: doc.id,
      data: doc.data()
    }))
  } catch (error) {
    console.error('Error fetching endorsers:', error)
  } finally {
    loading.value = false
  }
}

/**
 * Fetch all payouts from Firestore
 */
const fetchPayouts = async () => {
  payoutsLoading.value = true
  payoutsError.value = null
  try {
    const payoutsRef = collection(db, 'endorsers_payouts')
    const q = query(payoutsRef, orderBy('requestedAt', 'desc'))
    const snapshot = await getDocs(q)
    payouts.value = snapshot.docs.map(doc => ({
      id: doc.id,
      data: doc.data()
    }))
  } catch (error) {
    console.error('Error fetching payouts:', error)
    payoutsError.value = error.message || 'Failed to fetch payouts'
  } finally {
    payoutsLoading.value = false
  }
}

/**
 * Open edit modal with endorser data
 */
const openEditModal = (endorser) => {
  editingEndorserId.value = endorser.id
  editFormData.value = {
    firstName: endorser.data.firstName || '',
    lastName: endorser.data.lastName || '',
    email: endorser.data.email || '',
    personalEmail: endorser.data.personalEmail || '',
    commissionAmount: endorser.data.commissionAmount || 0,
    discountAmount: endorser.data.discountAmount || 0,
    maxCoupons: endorser.data.maxCoupons || 0,
    payoutThreshold: endorser.data.payoutThreshold || 0
  }
  isEditModalOpen.value = true
}

/**
 * Close edit modal
 */
const closeEditModal = () => {
  if (isUpdating.value) return
  isEditModalOpen.value = false
  editingEndorserId.value = null
  editFormData.value = {
    firstName: '',
    lastName: '',
    email: '',
    personalEmail: '',
    commissionAmount: 0,
    discountAmount: 0,
    maxCoupons: 0,
    payoutThreshold: 0
  }
}

/**
 * Handle update endorser
 */
const handleUpdateEndorser = async () => {
  if (isUpdating.value) return
  
  // Validate form data
  if (!editFormData.value.firstName || editFormData.value.firstName.trim() === '') {
    alert('First name is required')
    return
  }
  
  if (!editFormData.value.lastName || editFormData.value.lastName.trim() === '') {
    alert('Last name is required')
    return
  }
  
  if (!editFormData.value.personalEmail || editFormData.value.personalEmail.trim() === '') {
    alert('Personal email is required')
    return
  }
  
  // Basic email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(editFormData.value.personalEmail)) {
    alert('Please enter a valid email address')
    return
  }
  
  if (!editFormData.value.commissionAmount || editFormData.value.commissionAmount <= 0) {
    alert('Commission amount must be a positive number')
    return
  }
  
  if (!editFormData.value.discountAmount || editFormData.value.discountAmount <= 0) {
    alert('Discount amount must be a positive number')
    return
  }
  
  if (!editFormData.value.maxCoupons || editFormData.value.maxCoupons <= 0) {
    alert('Max coupons must be a positive number')
    return
  }
  
  if (editFormData.value.payoutThreshold === null || editFormData.value.payoutThreshold === undefined || editFormData.value.payoutThreshold < 0) {
    alert('Payout threshold must be a non-negative number')
    return
  }
  
  isUpdating.value = true
  
  try {
    const endorserRef = doc(db, 'endorsers', editingEndorserId.value)
    
    const updates = {
      firstName: editFormData.value.firstName.trim(),
      lastName: editFormData.value.lastName.trim(),
      personalEmail: editFormData.value.personalEmail.trim(),
      commissionAmount: editFormData.value.commissionAmount,
      discountAmount: editFormData.value.discountAmount,
      maxCoupons: editFormData.value.maxCoupons,
      payoutThreshold: editFormData.value.payoutThreshold
    }
    
    await updateDoc(endorserRef, updates)
    
    // Update local state
    const endorserIndex = endorsers.value.findIndex(e => e.id === editingEndorserId.value)
    if (endorserIndex !== -1) {
      endorsers.value[endorserIndex].data.firstName = updates.firstName
      endorsers.value[endorserIndex].data.lastName = updates.lastName
      endorsers.value[endorserIndex].data.personalEmail = updates.personalEmail
      endorsers.value[endorserIndex].data.commissionAmount = updates.commissionAmount
      endorsers.value[endorserIndex].data.discountAmount = updates.discountAmount
      endorsers.value[endorserIndex].data.maxCoupons = updates.maxCoupons
      endorsers.value[endorserIndex].data.payoutThreshold = updates.payoutThreshold
    }
    
    // Reset updating state and close modal
    isUpdating.value = false
    closeEditModal()
  } catch (error) {
    console.error('Error updating endorser:', error)
    alert(`Failed to update endorser: ${error.message || 'Unknown error'}`)
    isUpdating.value = false
  }
}

// Fetch endorsers on mount
onMounted(() => {
  fetchEndorsers()
  fetchPayouts()
  setupAllUsedCouponsListener()
})

onUnmounted(() => {
  if (unsubscribeAllUsedCoupons) unsubscribeAllUsedCoupons()
})
</script>

<style scoped>
/* Add any component-specific styles here */
</style>
